if (!customElements.get('mx-product-wheel')) {
  customElements.define('mx-product-wheel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.stage = this.querySelector('[data-wheel-stage]');
      this.items = [...this.querySelectorAll('[data-wheel-item]')];
      this.panels = [...this.querySelectorAll('[data-wheel-panel]')];
      this.total = this.items.length;
      if (!this.total) return;
      this.position = Math.min(Math.max(Number(this.dataset.start) || 0, 0), this.total - 1);
      this.target = this.position;
      this.focused = -1;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      const options = {signal: this.controller.signal};
      this.querySelector('[data-wheel-prev]').addEventListener('click', () => this.move(-1), options);
      this.querySelector('[data-wheel-next]').addEventListener('click', () => this.move(1), options);
      this.stage.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(event.key === 'ArrowRight' ? 1 : -1);
        }
      }, options);
      this.stage.addEventListener('pointerdown', event => {
        if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0) || this.total < 2) return;
        cancelAnimationFrame(this.frame);
        this.drag = {id: event.pointerId, x: event.clientX, y: event.clientY, position: this.position, moved: false};
      }, options);
      this.stage.addEventListener('pointermove', event => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        const dx = event.clientX - this.drag.x;
        if (!this.drag.moved && Math.abs(dx) < 8) return;
        if (!this.drag.moved && Math.abs(event.clientY - this.drag.y) > Math.abs(dx)) return;
        this.drag.moved = true;
        this.stage.setPointerCapture(event.pointerId);
        this.position = this.drag.position - dx / Math.max(90, this.stage.clientWidth * .16);
        this.draw();
      }, options);
      const finish = event => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        this.suppressClick = this.drag.moved;
        this.target = Math.round(this.position);
        this.drag = null;
        if (this.stage.hasPointerCapture(event.pointerId)) this.stage.releasePointerCapture(event.pointerId);
        this.animate();
      };
      this.stage.addEventListener('pointerup', finish, options);
      this.stage.addEventListener('pointercancel', finish, options);
      this.stage.addEventListener('pointerleave', event => {
        if (this.drag && !this.drag.moved) finish(event);
      }, options);
      this.stage.addEventListener('click', event => {
        if (this.suppressClick) {event.preventDefault(); this.suppressClick = false; return;}
        const item = event.target.closest('[data-wheel-item]');
        if (!item) return;
        const index = Number(item.dataset.wheelItem);
        if (index === this.wrap(Math.round(this.position)) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        let delta = index - this.wrap(Math.round(this.position));
        if (delta > this.total / 2) delta -= this.total;
        if (delta < -this.total / 2) delta += this.total;
        this.target = Math.round(this.position) + delta;
        this.animate();
      }, options);
      this.stage.addEventListener('dragstart', event => event.preventDefault(), options);
      this.addEventListener('change', event => {
        const select = event.target.closest('.mx-card-size');
        if (!select) return;
        const panel = select.closest('[data-wheel-panel]');
        const image = this.items[Number(panel.dataset.wheelPanel)].querySelector('img');
        const selected = select.selectedOptions[0];
        if (image && selected?.dataset.image) {
          image.src = selected.dataset.image;
          image.srcset = selected.dataset.srcset;
        }
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.draw());
      this.resizeObserver.observe(this.stage);
      this.dataset.ready = 'true';
      this.draw();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
    }
    wrap(value) {return ((value % this.total) + this.total) % this.total;}
    move(direction) {
      this.target = Math.round(this.target) + direction;
      this.animate();
    }
    animate() {
      cancelAnimationFrame(this.frame);
      if (this.motion.matches) {this.position = this.target; this.draw(); return;}
      let previous;
      const tick = time => {
        const elapsed = previous ? Math.min(time - previous, 50) : 16.7;
        previous = time;
        this.position += (this.target - this.position) * (1 - Math.exp(-elapsed / 110));
        if (Math.abs(this.target - this.position) < .001) {
          this.position = this.target;
          this.draw();
          return;
        }
        this.draw();
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }
    draw() {
      if (!this.stage || !this.total) return;
      const width = this.stage.clientWidth;
      const itemWidth = this.items[0]?.offsetWidth || 210;
      const radius = width < 700 ? width * .44 : Math.max(width * .4, (width / 2 - itemWidth * .32 - 24) * 1.18);
      const depth = width < 700 ? 210 : 290;
      const current = this.wrap(Math.round(this.position));
      this.items.forEach((item, index) => {
        const angle = (index - this.position) * Math.PI * 2 / this.total;
        const cosine = Math.cos(angle);
        const sine = Math.sin(angle);
        const x = sine * radius;
        const y = (cosine - 1) * 58;
        const z = (cosine - 1) * depth;
        const scale = .78 + cosine * .35;
        item.style.transform = `translate(-50%,-50%) translate3d(${x}px,${y}px,${z}px) rotateY(${-sine * 20}deg) scale(${scale})`;
        item.style.opacity = String(.16 + (cosine + 1) * .42);
        item.style.zIndex = String(Math.round((cosine + 1) * 100));
        item.style.pointerEvents = cosine > -.15 ? 'auto' : 'none';
        item.dataset.front = String(index === current);
        item.tabIndex = index === current ? 0 : -1;
        item.setAttribute('aria-hidden', String(index !== current));
      });
      if (this.focused !== current) {
        const activeElement = document.activeElement;
        if (activeElement?.matches('[data-wheel-item]') && this.stage.contains(activeElement)) this.stage.focus({preventScroll: true});
        this.panels.forEach((panel, index) => {
          if (index !== current && panel.contains(activeElement)) this.stage.focus({preventScroll: true});
          panel.hidden = index !== current;
          panel.inert = index !== current;
        });
        this.focused = current;
        this.querySelector('[data-wheel-position]').textContent = `${String(current + 1).padStart(2, '0')} / ${String(this.total).padStart(2, '0')}`;
        const title = this.panels[current].querySelector('.mx-wheel__title').textContent;
        this.querySelector('[data-wheel-status]').textContent = title;
      }
      this.querySelector('[data-wheel-prev]').disabled = this.total < 2;
      this.querySelector('[data-wheel-next]').disabled = this.total < 2;
    }
  });
}
